// src/pages/ExamPass.jsx — v4d
// #7 Responsive mobile : header, timer, barre action, footer nav, grille Qno
// #9 Découpage : ExamHeader, QuestionCard, LearnSolution, QnoGrid extraits
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Typography, Button, Stack, Divider, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Collapse,
  Select, MenuItem, FormControl, InputLabel, Tooltip, alpha, Paper,
  useMediaQuery, useTheme, IconButton,
} from "@mui/material";
import { PieChart, Pie, Cell, Legend, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import CancelIcon       from "@mui/icons-material/Cancel";
import HelpOutlineIcon  from "@mui/icons-material/HelpOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon    from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LightbulbIcon    from "@mui/icons-material/Lightbulb";
import EmojiEventsIcon  from "@mui/icons-material/EmojiEvents";
import MenuBookIcon     from "@mui/icons-material/MenuBook";
import ReplayIcon       from "@mui/icons-material/Replay";
import DownloadIcon     from "@mui/icons-material/Download";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import FlagIcon         from "@mui/icons-material/Flag";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import API from "../api";
import LabQuestion from "../components/LabQuestion";
import { toAbsoluteUrl } from "../utils/url";

// ── Palette ──────────────────────────────────────────────────────────────────
const P = {
  navy:  "#1e3a5f", navyL:"#243b5c",
  red:   "#c0392b", redL:"#e74c3c",
  green: "#27ae60", greenL:"#2ecc71",
  amber: "#d97706",
  teal:  { 500:"#14b8a6", 600:"#0d9488", 700:"#0f766e" },
  indigo:{ 400:"#818cf8", 500:"#6366f1", 600:"#4f46e5" },
  slate: { 50:"#f8fafc", 100:"#f1f5f9", 200:"#e2e8f0", 300:"#cbd5e1",
           400:"#94a3b8", 500:"#64748b", 700:"#334155", 800:"#1e293b" },
  white:"#ffffff",
};

const DEFAULT_DURATION = 30;
const DEFAULT_PQ_SEC   = 0;

function createStore(examId) {
  const KEY = `examv4_${examId}`;
  return {
    load:  () => { try { return JSON.parse(localStorage.getItem(KEY)||"{}"); } catch { return {}; } },
    save:  (o) => localStorage.setItem(KEY, JSON.stringify(o)),
    clear: () => localStorage.removeItem(KEY),
  };
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : Timer
// ═══════════════════════════════════════════════════════════════════
function Timer({ startedAtMs, durationMinutes, onTimeUp }) {
  const [left, setLeft] = useState(() => {
    const elapsed = Math.floor((Date.now() - startedAtMs) / 1000);
    return Math.max(durationMinutes * 60 - elapsed, 0);
  });
  useEffect(() => {
    const id = setInterval(() => setLeft(p => {
      const n = Math.max(p-1,0);
      if (n===0) { clearInterval(id); onTimeUp?.(); }
      return n;
    }), 1000);
    return () => clearInterval(id);
  }, [onTimeUp]);
  const urgent = left <= 60;
  const hh = Math.floor(left/3600);
  const mm = Math.floor((left%3600)/60);
  const ss = String(left%60).padStart(2,"0");
  const display = hh > 0 ? `${hh}:${String(mm).padStart(2,"0")}:${ss}` : `${mm}:${ss}`;
  return (
    <Typography sx={{
      color: urgent ? P.redL : P.redL,
      fontWeight:800,
      fontSize:{ xs:"0.85rem", sm:"1rem" },
      fontFamily:"'Courier New', monospace",
      animation: urgent ? "pulse 1s infinite" : "none",
      "@keyframes pulse": { "0%,100%":{ opacity:1 }, "50%":{ opacity:.5 } },
    }}>
      ⏱ {display}
    </Typography>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : ExamHeader
// Header navy sombre avec titre + timer + bouton Terminer
// ═══════════════════════════════════════════════════════════════════
function ExamHeader({ title, learnMode, startedAtMs, durationMinutes, onFinish, onTimeUp, tabSwitches=0, maxTabSwitches=null, t }) {
  return (
    <Box sx={{ background:P.navy, color:"white", borderRadius:"10px 10px 0 0", overflow:"hidden" }}>
      <Box sx={{
        px:{ xs:1.5, sm:3 }, py:{ xs:1.25, sm:2 },
        display:"flex", alignItems:"center",
        justifyContent:"space-between", gap:1,
      }}>
        {/* Titre — tronqué sur mobile */}
        <Typography sx={{
          fontWeight:800,
          fontSize:{ xs:"0.82rem", sm:"1.05rem" },
          flex:"1 1 0", minWidth:0,
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
        }}>
          {title}
        </Typography>

        {!learnMode && (
          <Stack direction="row" alignItems="center" spacing={{ xs:.75, sm:1.5 }} flexShrink={0}>
            {maxTabSwitches !== null && (
              <Typography sx={{
                fontSize:{ xs:"0.72rem", sm:"0.8rem" }, fontWeight:700,
                color: tabSwitches >= maxTabSwitches ? "#ff6b6b"
                     : tabSwitches >= maxTabSwitches - 1 ? "#fbbf24"
                     : "rgba(255,255,255,.6)",
                display:"flex", alignItems:"center", gap:.4,
              }}>
                👁 {tabSwitches}/{maxTabSwitches}
              </Typography>
            )}
            <Timer startedAtMs={startedAtMs} durationMinutes={durationMinutes} onTimeUp={onTimeUp} />
            {/* Libellé masqué sur très petit écran */}
            <Button variant="contained" endIcon={<ArrowForwardIcon />}
              onClick={onFinish}
              sx={{
                background:P.red, "&:hover":{ background:P.redL },
                textTransform:"none", fontWeight:700, borderRadius:"6px",
                py:.6, px:{ xs:1, sm:2 },
                fontSize:{ xs:"0.72rem", sm:"0.875rem" },
                minWidth:0,
                "& .MuiButton-endIcon": { display:{ xs:"none", sm:"inline-flex" } },
              }}>
              <Box component="span" sx={{ display:{ xs:"none", sm:"inline" } }}>{t("exam.finishTest")}</Box>
              <Box component="span" sx={{ display:{ xs:"inline", sm:"none" } }}>{t("exam.finishTestShort")}</Box>
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : AnswerOption
// ═══════════════════════════════════════════════════════════════════
function AnswerOption({ label, selected, onClick, disabled, type="radio", status }) {
  // status: null | "correct" | "wrong" | "correct-answer"
  const borderColor = status==="correct"        ? P.green
                    : status==="wrong"           ? P.red
                    : status==="correct-answer"  ? P.green
                    : selected ? P.navy          : P.slate[300];
  const bg = status==="correct"        ? alpha(P.green, .09)
           : status==="wrong"           ? alpha(P.red,   .08)
           : status==="correct-answer"  ? alpha(P.green, .05)
           : selected ? alpha(P.navy,.05) : P.white;
  return (
    <Box onClick={!disabled ? onClick : undefined} sx={{
      display:"flex", alignItems:"flex-start", gap:1.5,
      px:{ xs:1.5, sm:2 }, py:{ xs:1.25, sm:1.5 },
      borderRadius:"8px", cursor:disabled?"default":"pointer",
      border:`1.5px solid ${borderColor}`, background:bg,
      transition:"all .12s",
      "&:hover": disabled ? {} : { borderColor:P.navy, background:alpha(P.navy,.04) },
    }}>
      {/* Radio/checkbox indicator */}
      <Box sx={{
        width:18, height:18, flexShrink:0, mt:.1,
        borderRadius: type==="radio" ? "50%" : "4px",
        border:`2px solid ${
          status==="correct" || status==="correct-answer" ? P.green
          : status==="wrong" ? P.red
          : selected ? P.navy : P.slate[300]
        }`,
        background: status==="correct" ? P.green : status==="wrong" ? P.red : selected ? P.navy : "transparent",
        display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        {(selected || status==="correct-answer") && (
          <Box sx={{ width:7, height:7, borderRadius: type==="radio"?"50%":"2px",
            background: status==="correct-answer" ? P.green : "white" }} />
        )}
      </Box>
      <Typography variant="body2" sx={{
        flexGrow:1,
        fontWeight: (selected || status==="correct-answer") ? 600 : 400,
        color: status==="correct-answer" ? P.green : "inherit",
        lineHeight:1.5,
        fontSize:{ xs:"0.875rem", sm:"0.9rem" },
      }}>{label}</Typography>
      {status==="correct"        && <CheckCircleIcon sx={{ fontSize:18, color:P.green,  flexShrink:0, mt:.1 }} />}
      {status==="wrong"          && <CancelIcon      sx={{ fontSize:18, color:P.red,    flexShrink:0, mt:.1 }} />}
      {status==="correct-answer" && <CheckCircleIcon sx={{ fontSize:18, color:P.green,  flexShrink:0, mt:.1, opacity:.7 }} />}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : MatchingQuestion
// ═══════════════════════════════════════════════════════════════════
function MatchingQuestion({ lefts, rights, value, onChange, disabled }) {
  return (
    <Stack spacing={1.5}>
      {lefts.map(left => (
        <Stack key={left} direction={{ xs:"column", sm:"row" }} spacing={1} alignItems={{ sm:"center" }}>
          <Box sx={{ px:2, py:1, minWidth:{ sm:140 }, borderRadius:"6px",
            background:alpha(P.navy,.05), border:`1px solid ${alpha(P.navy,.2)}` }}>
            <Typography variant="body2" fontWeight={600}>{left}</Typography>
          </Box>
          <Typography sx={{ color:P.slate[400], fontSize:"1.1rem", display:{ xs:"none", sm:"block" } }}>→</Typography>
          <Box component="select" disabled={disabled} value={value[left]||""}
            onChange={e => onChange({ ...value, [left]:e.target.value })}
            sx={{ flex:1, px:1.5, py:1, borderRadius:"6px", fontSize:"0.875rem",
                  border:`1.5px solid ${value[left]?P.navy:P.slate[300]}`,
                  background: value[left]?alpha(P.navy,.04):"white",
                  outline:"none", cursor:disabled?"not-allowed":"pointer",
                  width:"100%" }}>
            <option value="">— Sélectionner —</option>
            {rights.map(r => <option key={r} value={r}>{r}</option>)}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : LearnSolution
// Bloc feedback après validation en mode apprentissage
// ═══════════════════════════════════════════════════════════════════
function LearnSolution({ learnResult, question, t }) {
  if (!learnResult) return null;
  const q = question;
  return (
    <Box sx={{ px:{ xs:2, sm:3 }, py:2.5, borderTop:`1px solid ${P.slate[200]}` }}>
      {/* Badge verdict */}
      <Box sx={{
        display:"inline-flex", alignItems:"center", gap:1,
        px:2, py:.75, borderRadius:"8px", mb:2,
        background: learnResult.correct ? alpha(P.green,.1) : alpha(P.red,.1),
        border: `1.5px solid ${learnResult.correct ? P.green : P.red}`,
      }}>
        {learnResult.correct
          ? <CheckCircleIcon sx={{ color:P.green, fontSize:20 }} />
          : <CancelIcon      sx={{ color:P.red,   fontSize:20 }} />
        }
        <Typography sx={{ fontWeight:800, color: learnResult.correct ? P.green : P.red, fontSize:"0.95rem" }}>
          {learnResult.correct ? t("exam.correctAnswer") : t("exam.wrongAnswer")}
        </Typography>
      </Box>

      {/* Bonne réponse si incorrect */}
      {!learnResult.correct && (
        <Box sx={{ mb:2, p:1.5, borderRadius:"8px", background:alpha(P.green,.06), border:`1px solid ${alpha(P.green,.3)}` }}>
          <Typography variant="caption" sx={{ fontWeight:700, color:P.green, textTransform:"uppercase", letterSpacing:"0.07em", display:"block", mb:.5 }}>
            {t("exam.correctAnswerLabel")}
          </Typography>
          <Typography variant="body2" sx={{ color:P.slate[700], fontWeight:600 }}>
            {Array.isArray(learnResult.correct_answer)
              ? learnResult.correct_answer.join(", ")
              : typeof learnResult.correct_answer === "object"
                ? Object.entries(learnResult.correct_answer||{}).map(([l,r])=>`${l} → ${r}`).join(" | ")
                : String(learnResult.correct_answer || "")
            }
          </Typography>
        </Box>
      )}

      {/* Explication */}
      {(learnResult.explanation || q?.explanation) ? (
        <Stack direction="row" spacing={1.5} alignItems="flex-start"
          sx={{ p:2, borderRadius:"8px", background:alpha(P.indigo[500],.05), border:`1px solid ${alpha(P.indigo[500],.2)}` }}>
          <LightbulbIcon sx={{ color:P.indigo[600], fontSize:18, mt:.2, flexShrink:0 }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight:700, color:P.indigo[600], textTransform:"uppercase", letterSpacing:"0.07em", display:"block", mb:.5 }}>
              {t("exam.explanation")}
            </Typography>
            <Typography variant="body2" sx={{ color:P.slate[700] }}>
              {learnResult.explanation || q?.explanation}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle:"italic" }}>
          {t("exam.noExplanation")}
        </Typography>
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : QuestionCard
// Corps principal de la question (meta, texte, options, barre action, solution)
// ═══════════════════════════════════════════════════════════════════
function QuestionCard({
  q, idx, questions, isMatch, isMulti, isLab,
  choices, answers, multiSelection, currentSelection,
  learnMode, validated, learnFeedback, learnResult, checking, error,
  unanswered, setIdx,
  flagged = new Set(), onFlag,
  onAnswer, onValidate, onNext, onClearSelection,
  startedAtMs, durationMinutes, doSubmit, t,
}) {
  const isFlagged = q && flagged.has(q.id);
  const isLast = questions.length > 0 && idx === questions.length - 1;

  return (
    <Box sx={{ background:"white", border:`1px solid ${P.slate[200]}`, borderTop:"none", borderRadius:"0 0 10px 10px" }}>

      {/* ── Meta row : numéro + timer + total ── */}
      <Box sx={{ px:{ xs:1.5, sm:3 }, py:{ xs:1.25, sm:2 }, borderBottom:`1px solid ${P.slate[200]}`, background:P.slate[50] }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography sx={{ fontWeight:700, color:P.slate[700], fontSize:{ xs:"0.82rem", sm:"0.95rem" } }}>
            {t("exam.question")} {idx+1}
          </Typography>
          {/* Barre de progression mobile */}
          <Box sx={{ flex:1, mx:{ xs:1, sm:2 }, display:{ xs:"block", sm:"none" } }}>
            <Box sx={{ height:4, borderRadius:2, background:P.slate[200] }}>
              <Box sx={{ height:"100%", borderRadius:2, background:P.navy,
                width:`${Math.round(((idx+1)/questions.length)*100)}%`,
                transition:"width .3s" }} />
            </Box>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight:600, color:P.slate[500], fontSize:{ xs:"0.78rem", sm:"0.9rem" } }}>
              / {questions.length}
            </Typography>
            {/* Bouton flag */}
            <Tooltip title={isFlagged ? t("exam.removeBookmark") : t("exam.markReview")}>
              <IconButton size="small" onClick={() => q && onFlag?.(q.id)}
                sx={{ color: isFlagged ? P.amber : P.slate[300],
                  "&:hover":{ color: isFlagged ? P.red : P.amber },
                  transition:"color .15s", p:.5 }}>
                {isFlagged ? <FlagIcon fontSize="small" /> : <FlagOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* ── Texte de la question ── */}
      <Box sx={{ px:{ xs:2, sm:3 }, py:{ xs:2, sm:3 } }}>
        {!isLab && (
          <Typography variant="body1" sx={{
            lineHeight:1.75, color:P.slate[800], mb:{ xs:2, sm:3 }, fontWeight:500,
            fontSize:{ xs:"0.92rem", sm:"1rem" },
          }}>
            {q.content||q.text}
          </Typography>
        )}

        {q.image_url && !isMatch && (
          <Box sx={{ mb:2.5, borderRadius:"6px", overflow:"hidden", border:`1px solid ${P.slate[200]}`, display:"block" }}>
            <img alt="illustration" src={toAbsoluteUrl(q.image_url)}
              style={{ maxHeight:200, maxWidth:"100%", objectFit:"contain", display:"block" }} />
          </Box>
        )}

        {/* LAB */}
        {isLab && q.lab_data && (
          <LabQuestion
            question={q}
            readOnly={learnMode && validated}
            hideScenario={true}
            onComplete={(done) => {
              if (done) {
                if (learnMode && !validated) {
                  onAnswer(q.id, "__lab_done__");
                  setTimeout(() => onValidate(), 300);
                } else if (!learnMode) {
                  onAnswer(q.id, "__lab_done__");
                }
              }
            }}
          />
        )}

        {!isLab && <Divider sx={{ mb:2.5 }} />}

        {/* ── Options de réponse ── */}
        {!isLab && isMatch ? (
          <MatchingQuestion lefts={q.matching_lefts||[]} rights={q.matching_rights||[]}
            value={answers[q.id]||{}} onChange={m=>onAnswer(q.id,m)} disabled={validated} />
        ) : isMulti ? (
          <Stack spacing={{ xs:.75, sm:1 }}>
            {choices.map((opt,i) => {
              let learnStatus = null;
              if (learnMode && learnFeedback && learnResult) {
                const correctList = Array.isArray(learnResult.correct_answer) ? learnResult.correct_answer : [];
                const isThisCorrect = correctList.includes(opt);
                const isSelected    = multiSelection.includes(i);
                if (isSelected && isThisCorrect)    learnStatus = "correct";
                else if (isSelected && !isThisCorrect) learnStatus = "wrong";
                else if (!isSelected && isThisCorrect) learnStatus = "correct-answer";
              }
              return (
                <AnswerOption key={i} type="checkbox"
                  label={opt} selected={multiSelection.includes(i)}
                  onClick={() => !validated && onAnswer(q.id, multiSelection.includes(i)
                    ? multiSelection.filter(x=>x!==i)
                    : [...multiSelection, i])}
                  disabled={validated}
                  status={learnStatus} />
              );
            })}
          </Stack>
        ) : (
          <Stack spacing={{ xs:.75, sm:1 }}>
            {choices.map((opt,i) => {
              const isSelected = currentSelection === opt;
              let learnStatus = null;
              if (learnMode && learnFeedback && learnResult) {
                const correctAns = learnResult.correct_answer;
                const isThisCorrect = opt === correctAns;
                if (isSelected && learnResult.correct)    learnStatus = "correct";
                else if (isSelected && !learnResult.correct) learnStatus = "wrong";
                else if (!isSelected && isThisCorrect)    learnStatus = "correct-answer";
              }
              return (
                <AnswerOption key={i} type="radio"
                  label={opt} selected={isSelected}
                  onClick={() => !validated && onAnswer(q.id,opt)} disabled={validated}
                  status={learnStatus} />
              );
            })}
          </Stack>
        )}

        {error && <Alert severity="warning" sx={{ mt:1.5, borderRadius:"8px" }}>{error}</Alert>}
      </Box>

      {/* ── Barre d'action ── */}
      <Box sx={{
        px:{ xs:2, sm:3 }, py:{ xs:1.5, sm:2 },
        background:P.slate[50], borderTop:`1px solid ${P.slate[200]}`,
        display:"flex", justifyContent:"center", gap:1.5, alignItems:"center",
      }}>
        {learnMode ? (
          !validated ? (
            <Button variant="contained" onClick={onValidate} disabled={checking}
              startIcon={checking ? <CircularProgress size={16} color="inherit" /> : null}
              fullWidth
              sx={{ background:P.navy, "&:hover":{ background:P.navyL }, textTransform:"none",
                fontWeight:700, borderRadius:"8px", maxWidth:{ sm:320 } }}>
              {checking ? t("exam.validating") : `✓ ${t("exam.validate")}`}
            </Button>
          ) : (
            <Button variant="outlined" onClick={onClearSelection} fullWidth
              sx={{ borderColor:P.slate[300], color:P.slate[600], textTransform:"none",
                borderRadius:"8px", maxWidth:{ sm:280 } }}>
              {t("exam.clearSelection")}
            </Button>
          )
        ) : (
          <Button variant="outlined" onClick={onClearSelection} fullWidth
            sx={{ borderColor:P.slate[300], color:P.slate[600], textTransform:"none",
              borderRadius:"8px", maxWidth:{ sm:280 } }}>
            {t("exam.clearSelection")}
          </Button>
        )}
      </Box>

      {/* ── Solution (learn mode après validation) ── */}
      {learnMode && learnFeedback && learnResult && (
        <LearnSolution learnResult={learnResult} question={q} t={t} />
      )}

      {/* ── Navigation footer ── */}
      <Box sx={{ px:{ xs:1.5, sm:3 }, py:{ xs:1.25, sm:2 }, borderTop:`1px solid ${P.slate[200]}` }}>
        <Stack spacing={1}>
          {/* Bouton "Question suivante" — plein écran sur mobile */}
          <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={onNext} fullWidth
            sx={{ background:P.navy, "&:hover":{ background:P.navyL }, textTransform:"none",
              fontWeight:700, borderRadius:"8px" }}>
            {isLast ? (learnMode?t("exam.viewResults"):t("exam.finishTest")) : t("exam.nextQuestion")}
          </Button>

          {/* Dropdowns — côte à côte, même sur mobile */}
          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ flex:1 }}>
              <Select value={idx+1} onChange={e => setIdx(e.target.value-1)}
                sx={{ fontSize:"0.82rem", color:P.red, fontWeight:600,
                  "& .MuiOutlinedInput-notchedOutline":{ borderColor:P.slate[300] } }}>
                {questions.map((_,i) => (
                  <MenuItem key={i} value={i+1}>{t("exam.goTo")} {i+1}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {!learnMode && unanswered.length > 0 && (
              <FormControl size="small" sx={{ flex:1 }}>
                <Select value="" displayEmpty onChange={e => setIdx(e.target.value)}
                  sx={{ fontSize:"0.82rem",
                    "& .MuiOutlinedInput-notchedOutline":{ borderColor:P.slate[300] } }}>
                  <MenuItem value="" disabled>{t("exam.unanswered")} : {unanswered.length}</MenuItem>
                  {unanswered.map(({ i }) => (
                    <MenuItem key={i} value={i}>{t("exam.goTo")} {i+1}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {flagged.size > 0 && (
              <FormControl size="small" sx={{ flex:1 }}>
                <Select value="" displayEmpty onChange={e => setIdx(e.target.value)}
                  sx={{ fontSize:"0.82rem", color:P.amber, fontWeight:600,
                    "& .MuiOutlinedInput-notchedOutline":{ borderColor:alpha(P.amber,.4) } }}>
                  <MenuItem value="" disabled>
                    🚩 {t("exam.bookmarks")} : {flagged.size}
                  </MenuItem>
                  {questions.map((qs, i) => flagged.has(qs.id) ? (
                    <MenuItem key={i} value={i}>🚩 {t("exam.goTo")} {i+1}</MenuItem>
                  ) : null).filter(Boolean)}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : QnoGrid
// Grille cliquable des numéros de questions avec statut ✓/✗/?
// ═══════════════════════════════════════════════════════════════════
function QnoGrid({ questions, detailMap, answeredIds, reviewIdx, onSelect, flagged=new Set() }) {
  return (
    <Box sx={{
      display:"grid",
      gridTemplateColumns:{ xs:"repeat(auto-fill, minmax(68px,1fr))", sm:"repeat(auto-fill, minmax(80px,1fr))" },
      gap:{ xs:.5, sm:.75 },
    }}>
      {questions.map((q,i) => {
        const det     = detailMap[q.id];
        const inDetails = answeredIds.has(q.id);
        const isEmpty = !inDetails;
        const symbol = isEmpty ? "?" : det?.correct===true ? "✓" : "✗";
        const symColor = isEmpty ? P.slate[400] : det?.correct===true ? P.green : P.red;
        const isActive = reviewIdx === i;
        const isQFlagged = flagged.has(q.id);
        return (
          <Box key={q.id} onClick={() => onSelect(i)} sx={{
            display:"flex", alignItems:"center", justifyContent:"center", gap:.4,
            cursor:"pointer", py:{ xs:.4, sm:.3 }, px:.5, borderRadius:"6px",
            background: isActive ? alpha(symColor, .1) : isQFlagged ? alpha(P.amber,.08) : "transparent",
            border: isActive ? `1px solid ${alpha(symColor,.3)}`
                  : isQFlagged ? `1px solid ${alpha(P.amber,.35)}`
                  : "1px solid transparent",
            transition:"all .1s",
            "&:hover": { background:alpha(P.navy,.05) },
          }}>
            {isQFlagged && (
              <Typography sx={{ fontSize:"0.65rem", lineHeight:1 }}>🚩</Typography>
            )}
            <Typography sx={{ fontSize:{ xs:"0.72rem", sm:"0.8rem" }, color:P.slate[700], fontWeight:500, whiteSpace:"nowrap" }}>
              Q{i+1}
            </Typography>
            <Typography sx={{ fontSize:{ xs:"0.72rem", sm:"0.8rem" }, color:symColor, fontWeight:800 }}>
              {symbol}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOUS-COMPOSANT : ConfirmDialog
// ═══════════════════════════════════════════════════════════════════
function ConfirmDialog({ open, answered, total, skipped, onConfirm, onCancel, submitting, t }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth
      PaperProps={{ sx:{ borderRadius:"12px", mx:2 } }}>
      <DialogTitle sx={{ fontWeight:800, background:P.navy, color:"white", py:2 }}>
        {t("exam.confirmFinish.title")}
      </DialogTitle>
      <DialogContent sx={{ pt:3 }}>
        <Stack spacing={2}>
          <Stack direction="row" sx={{ border:`1px solid ${P.slate[200]}`, borderRadius:"8px", overflow:"hidden" }}>
            {[
              { v:answered, l:t("exam.confirmFinish.answered"),  c:P.green },
              { v:skipped,  l:t("exam.confirmFinish.skipped"),    c:skipped>0?P.amber:P.slate[400] },
              { v:total,    l:t("exam.confirmFinish.total"),      c:P.slate[700] },
            ].map((s,i) => (
              <Box key={s.l} sx={{ flex:1, textAlign:"center", py:2,
                background:i===0?alpha(P.green,.05):i===1&&skipped>0?alpha(P.amber,.05):"white",
                borderLeft:i>0?`1px solid ${P.slate[200]}`:"none" }}>
                <Typography variant="h4" fontWeight={800} sx={{ color:s.c, lineHeight:1 }}>{s.v}</Typography>
                <Typography variant="caption" sx={{ color:P.slate[500], fontWeight:600, textTransform:"uppercase", fontSize:"0.65rem" }}>{s.l}</Typography>
              </Box>
            ))}
          </Stack>
          {skipped > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ borderRadius:"8px" }}>
              <strong>{skipped} {t("exam.confirmFinish.warning")}</strong>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            {t("exam.confirmFinish.irreversible")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2.5, gap:1, flexDirection:{ xs:"column-reverse", sm:"row" } }}>
        <Button onClick={onCancel} variant="outlined" disabled={submitting}
          sx={{ width:{ xs:"100%", sm:"auto" } }}>
          {t("exam.confirmFinish.continue")}
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={submitting}
          sx={{ background:P.navy, "&:hover":{ background:P.navyL }, width:{ xs:"100%", sm:"auto" } }}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}>
          {submitting ? t("exam.confirmFinish.submitting") : t("exam.confirmFinish.finish")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// COMPOSANT : ExamReport (résultats post-exam)
// ═══════════════════════════════════════════════════════════════════
function ExamReport({ exam, questions, answers, result, passThreshold=50, onRéessayer, onGoResults, t }) {
  const [reviewIdx, setReviewIdx] = useState(null);

  const total     = questions.length;
  const detailMap = useMemo(() => {
    const m = {};
    (result?.details||[]).forEach(d => { m[d.question_id] = d; });
    return m;
  }, [result]);

  const answeredIds  = new Set((result?.details||[]).map(d => d.question_id));
  const correctCount = result?.details ? result.details.filter(d=>d.correct).length : Object.keys(answers).length;
  const wrongCount   = result?.details ? result.details.filter(d=>!d.correct).length : total - correctCount;
  const skippedCount = result?.details
    ? questions.filter(q => !answeredIds.has(q.id)).length
    : questions.filter(q => { const v = answers[q.id]; return !v && v!==0 || (Array.isArray(v)&&v.length===0); }).length;
  const pct      = result?.percentage ?? Math.round(correctCount/total*100);
  const score    = result?.score ?? correctCount;
  const maxScore = result?.max_score ?? total;

  const pieData = [
    { name:t("report.correct"),   value:correctCount, color:P.green },
    { name:t("report.incorrect"), value:wrongCount,    color:P.red },
  ];

  // ── Vue review d'une question ─────────────────────────────────
  if (reviewIdx !== null) {
    const q       = questions[reviewIdx];
    const det     = detailMap[q?.id];
    const userAns = answers[q?.id];
    const isEmpty = !userAns && userAns!==0 || (Array.isArray(userAns)&&userAns.length===0);
    const isCorrect = det?.correct ?? null;
    const choices = q?.choices?.length ? q.choices : [q?.choice_a,q?.choice_b,q?.choice_c,q?.choice_d].filter(Boolean);
    const isMatch = q?.type==="match"||q?.question_type==="matching";
    const isMulti = q?.type==="multi"||q?.question_type==="multiple";
    const multiSel = Array.isArray(userAns)?userAns:[];

    const getStatus = (opt, i) => {
      if (!det) return null;
      const selected = isMulti ? multiSel.includes(i) : userAns===opt;
      if (!selected) return null;
      return det.correct ? "correct" : "wrong";
    };

    return (
      <Box sx={{ maxWidth:760, mx:"auto", px:{ xs:1, sm:0 } }}>
        {/* Navigation review */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setReviewIdx(null)}
            sx={{ color:P.navy, fontWeight:700, textTransform:"none", fontSize:{ xs:"0.82rem", sm:"0.875rem" } }}>
            {t("results.title")}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setReviewIdx(i => Math.max(i-1,0))} disabled={reviewIdx===0}
              variant="outlined" size="small" sx={{ color:P.navy, borderColor:P.navy, minWidth:0, px:1.5 }}>←</Button>
            <Button onClick={() => setReviewIdx(i => Math.min(i+1,total-1))} disabled={reviewIdx===total-1}
              variant="outlined" size="small" sx={{ color:P.navy, borderColor:P.navy, minWidth:0, px:1.5 }}>→</Button>
          </Stack>
        </Stack>

        <Box sx={{ background:P.navy, color:"white", px:{ xs:2, sm:3 }, py:2, borderRadius:"10px 10px 0 0" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography sx={{ fontWeight:700, fontSize:{ xs:"0.85rem", sm:"0.95rem" },
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, minWidth:0 }}>
              {exam.title}
            </Typography>
            {/* Badge verdict compact */}
            <Box sx={{ display:"inline-flex", alignItems:"center", gap:.5,
              px:1.5, py:.5, borderRadius:"6px", flexShrink:0,
              background: isEmpty ? alpha(P.slate[400],.2) : isCorrect ? alpha(P.green,.2) : alpha(P.red,.2),
              border:`1px solid ${isEmpty?P.slate[300]:isCorrect?P.green:P.red}` }}>
              {isEmpty ? <HelpOutlineIcon sx={{ fontSize:14, color:P.slate[200] }} />
                : isCorrect ? <CheckCircleIcon sx={{ fontSize:14, color:P.green }} />
                : <CancelIcon sx={{ fontSize:14, color:P.red }} />}
              <Typography variant="caption" sx={{ fontWeight:700,
                color: isEmpty?P.slate[300]:isCorrect?P.green:P.red }}>
                {isEmpty?t("report.unanswered_short"):isCorrect?t("report.correct_short"):t("report.incorrect_short")}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ background:"white", border:`1px solid ${P.slate[200]}`, borderTop:"none",
          borderRadius:"0 0 10px 10px", p:{ xs:2, sm:3 } }}>
          <Typography sx={{ fontWeight:700, mb:2, fontSize:{ xs:"0.88rem", sm:"1rem" }, color:P.slate[700] }}>
            {t("exam.question")} {reviewIdx+1}
          </Typography>
          <Typography variant="body1" sx={{ mb:2.5, lineHeight:1.7, color:P.slate[700],
            fontSize:{ xs:"0.9rem", sm:"1rem" } }}>
            {q.content||q.text}
          </Typography>

          {(q.type==="lab"||q.question_type==="lab") && q.lab_data ? (
            <LabQuestion question={q} readOnly={true} />
          ) : isMatch ? (
            <MatchingQuestion lefts={q.matching_lefts||[]} rights={q.matching_rights||[]}
              value={userAns||{}} onChange={()=>{}} disabled={true} />
          ) : (
            <Stack spacing={{ xs:.75, sm:1 }}>
              {choices.map((opt,i) => (
                <AnswerOption key={i} label={opt}
                  selected={isMulti?multiSel.includes(i):userAns===opt}
                  disabled={true} type={isMulti?"checkbox":"radio"}
                  status={getStatus(opt,i)} />
              ))}
            </Stack>
          )}

          {q.explanation && (
            <Box sx={{ mt:3, p:2, borderRadius:"8px",
              background:alpha(P.indigo[500],.05), border:`1px solid ${alpha(P.indigo[500],.2)}` }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <LightbulbIcon sx={{ color:P.indigo[500], fontSize:18, mt:.2, flexShrink:0 }} />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight:700, textTransform:"uppercase",
                    color:P.indigo[600], letterSpacing:"0.08em" }}>{t("exam.explanation")}</Typography>
                  <Typography variant="body2" sx={{ mt:.5, color:P.slate[700] }}>{q.explanation}</Typography>
                </Box>
              </Stack>
            </Box>
          )}

          <Stack direction={{ xs:"column-reverse", sm:"row" }} justifyContent={{ sm:"space-between" }}
            alignItems={{ sm:"center" }} spacing={1} sx={{ mt:3 }}>
            <FormControl size="small" sx={{ width:{ xs:"100%", sm:"auto" }, minWidth:{ sm:160 } }}>
              <Select value={reviewIdx+1} onChange={e => setReviewIdx(e.target.value-1)}
                sx={{ fontSize:"0.85rem", color:P.red, fontWeight:600 }}>
                {questions.map((_,i) => (
                  <MenuItem key={i} value={i+1}>{t("exam.goTo")} {i+1}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} fullWidth={false}
              onClick={() => reviewIdx<total-1?setReviewIdx(i=>i+1):setReviewIdx(null)}
              sx={{ background:P.navy, "&:hover":{ background:P.navyL }, textTransform:"none",
                width:{ xs:"100%", sm:"auto" } }}>
              {reviewIdx<total-1?t("exam.nextQuestion"):t("report.myResults")}
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  // ── Page résultats principale ─────────────────────────────────
  return (
    <Box sx={{ maxWidth:760, mx:"auto", px:{ xs:1, sm:0 } }}>
      {/* Header */}
      <Box sx={{ background:P.navy, color:"white", px:{ xs:2, sm:3 }, py:2.5,
        borderRadius:"10px 10px 0 0", textAlign:"center" }}>
        <Typography sx={{ fontWeight:800, fontSize:{ xs:"0.95rem", sm:"1.1rem" } }}>{exam.title}</Typography>
      </Box>

      {/* Score + pie */}
      <Box sx={{ background:"white", border:`1px solid ${P.slate[200]}`, borderTop:"none",
        p:{ xs:2, sm:4 } }}>
        <Typography sx={{ textAlign:"center", color:P.red, fontWeight:800,
          fontSize:{ xs:"1rem", sm:"1.2rem" }, mb:3 }}>
          {t("report.title")}
        </Typography>

        {/* Pie chart */}
        <Box sx={{ height:{ xs:180, sm:220 }, mb:2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                label={({ name, percent }) => `${Math.round(percent*100)}%`}
                labelLine={false}>
                {pieData.map((entry,i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <RTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Stats — 2 colonnes sur mobile */}
        <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr 1fr", sm:"1fr" }, gap:.5,
          mb:3, maxWidth:{ sm:320 }, mx:"auto" }}>
          <Typography sx={{ fontWeight:700, color:P.slate[800], fontSize:{ xs:"0.88rem", sm:"1rem" } }}>
            {t("common.total")} : {total} {t("common.questions")}
          </Typography>
          <Typography sx={{ fontWeight:700, color:P.green, fontSize:{ xs:"0.88rem", sm:"1rem" } }}>
            {t("report.correct")} : {correctCount}
          </Typography>
          <Typography sx={{ fontWeight:700, color:P.red, fontSize:{ xs:"0.88rem", sm:"1rem" } }}>
            {t("report.incorrect")} : {wrongCount}
          </Typography>
          <Typography sx={{ fontWeight:600, color:P.slate[500], fontSize:{ xs:"0.82rem", sm:"0.95rem" } }}>
            {t("report.unanswered")} : {skippedCount}
          </Typography>
          <Typography sx={{ fontWeight:800, color:P.slate[800], fontSize:{ xs:"0.95rem", sm:"1.1rem" },
            gridColumn:{ xs:"1/-1", sm:"auto" } }}>
            {t("common.score")} : {score}/{maxScore} ({pct}%)
          </Typography>
        </Box>

        {/* Badge résultat */}
        {(() => {
          const threshold = passThreshold;
          const isPassed  = pct >= threshold;
          return (
            <Box sx={{ textAlign:"center", mb:2 }}>
              <Box sx={{
                display:"inline-flex", alignItems:"center", gap:1,
                px:3, py:1.25, borderRadius:"50px",
                background: isPassed ? alpha(P.green,.1) : alpha(P.red,.1),
                border:`2px solid ${isPassed ? P.green : P.red}`,
              }}>
                {isPassed
                  ? <WorkspacePremiumIcon sx={{ color:P.green, fontSize:22 }} />
                  : <CancelIcon          sx={{ color:P.red,   fontSize:22 }} />
                }
                <Typography sx={{ fontWeight:800, color:isPassed ? P.green : P.red, fontSize:"1rem" }}>
                  {isPassed ? t("report.passed") : t("report.failed")}
                </Typography>
              </Box>
              {!isPassed && (
                <Typography variant="caption" sx={{ display:"block", color:P.slate[500], mt:.75 }}>
                  {t("report.requiredScore")} : {threshold}% · {t("report.yourScore")} : {pct}%
                </Typography>
              )}
            </Box>
          );
        })()}

        <Stack direction={{ xs:"column", sm:"row" }} justifyContent="center" spacing={1.5} sx={{ mb:3 }}>
          <Button startIcon={<ReplayIcon />} onClick={onRéessayer} variant="outlined" fullWidth
            sx={{ borderColor:P.navy, color:P.navy, textTransform:"none", borderRadius:"8px", fontWeight:700,
              maxWidth:{ sm:200 } }}>
            {t("report.retry")}
          </Button>
          <Button startIcon={<EmojiEventsIcon />} onClick={onGoResults} variant="contained" fullWidth
            sx={{ background:P.navy, "&:hover":{ background:P.navyL }, textTransform:"none",
              borderRadius:"8px", fontWeight:700, maxWidth:{ sm:200 } }}>
            {t("report.myResults")}
          </Button>
          {pct >= passThreshold && result?.id && (
            <Button
              startIcon={<WorkspacePremiumIcon />}
              endIcon={<DownloadIcon />}
              variant="contained"
              fullWidth
              onClick={async () => {
                try {
                  const token = localStorage.getItem("access_token");
                  const res = await fetch(
                    `${process.env.REACT_APP_API_URL || "http://localhost:8001"}/attempts/${result.id}/certificate`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (!res.ok) throw new Error("Erreur téléchargement");
                  const blob = await res.blob();
                  const url  = URL.createObjectURL(blob);
                  const a    = document.createElement("a");
                  a.href     = url;
                  a.download = `certificat_${result.id}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch { alert(t("errors.loadFailed")); }
              }}
              sx={{ background:P.green, "&:hover":{ background:"#219a52" }, textTransform:"none",
                borderRadius:"8px", fontWeight:700, maxWidth:{ sm:220 } }}>
              {t("report.certificate")}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Grille Qno */}
      <Box sx={{ background:"white", border:`1px solid ${P.slate[200]}`, mt:1,
        borderRadius:"10px", p:{ xs:2, sm:3 } }}>
        <Typography sx={{ textAlign:"center", color:P.red, fontWeight:700,
          fontSize:{ xs:"0.85rem", sm:"0.95rem" }, mb:2 }}>
          {t("report.reviewTitle")}
        </Typography>

        {/* Légende */}
        <Stack direction="row" justifyContent="center" spacing={{ xs:1.5, sm:3 }} sx={{ mb:2 }}>
          {[
            { color:P.green,      symbol:"✓", label:t("report.correct") },
            { color:P.red,        symbol:"✗", label:t("report.incorrect") },
            { color:P.slate[400], symbol:"?", label:t("report.unanswered") },
          ].map(l => (
            <Stack key={l.label} direction="row" alignItems="center" spacing={.5}>
              <Typography sx={{ fontSize:"0.78rem", color:l.color, fontWeight:800 }}>{l.symbol}</Typography>
              <Typography sx={{ fontSize:{ xs:"0.7rem", sm:"0.75rem" }, color:P.slate[500] }}>{l.label}</Typography>
            </Stack>
          ))}
        </Stack>

        <QnoGrid
          questions={questions}
          detailMap={detailMap}
          answeredIds={answeredIds}
          reviewIdx={reviewIdx}
          onSelect={setReviewIdx}
        />
      </Box>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════
export default function ExamPass() {
  const { t, i18n } = useTranslation();
  const { id }         = useParams();
  const [searchParams] = useSearchParams();
  const examId         = Number(id);
  const navigate       = useNavigate();
  const learnMode      = searchParams.get("mode") === "learn";

  const store = useMemo(() => createStore(examId), [examId]);

  const [exam,          setExam]          = useState(null);
  const [questions,     setQuestions]     = useState([]);
  const [idx,           setIdx]           = useState(0);
  const [answers,       setAnswers]       = useState({});
  const [startedAtMs,   setStartedAtMs]   = useState(Date.now());
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState("");
  const [loadError,     setLoadError]     = useState("");
  const [confirmOpen,   setConfirmOpen]   = useState(false);
  const [phase,         setPhase]         = useState("exam"); // "exam" | "report"
  const [serverResult,  setServerResult]  = useState(null);
  const [learnFeedback, setLearnFeedback] = useState(false);
  const [validated,     setValidated]     = useState(false);
  const [learnResult,   setLearnResult]   = useState(null);
  const [checking,      setChecking]      = useState(false);
  const [learnDetails,  setLearnDetails]  = useState([]);
  const [labDoneIds,    setLabDoneIds]    = useState(new Set());
  const [flagged,       setFlagged]       = useState(new Set());  // IDs des questions flaggées
  const [resumePrompt,  setResumePrompt]  = useState(false);  // crash recovery
  const [draftLoaded,   setDraftLoaded]   = useState(false);
  const [attBlocked,    setAttBlocked]    = useState(false);   // limite tentatives atteinte
  const [attInfo,       setAttInfo]       = useState(null);    // { used, max }
  const [tabSwitches,   setTabSwitches]   = useState(0);       // compteur sorties d'onglet
  const [tabWarning,    setTabWarning]    = useState(false);   // afficher le warning

  const durationMinutes = exam && Number(exam.duration_minutes)>0    ? Number(exam.duration_minutes)     : DEFAULT_DURATION;
  const q       = questions[idx] || null;
  const isMatch = q?.type==="match"||q?.question_type==="matching";
  const isMulti = q?.type==="multi"||q?.question_type==="multiple";
  const isLab   = q?.type==="lab"  ||q?.question_type==="lab";

  const choices = useMemo(() => {
    if (!q||isMatch) return [];
    if (q.choices?.length) return q.choices;
    return [q.choice_a,q.choice_b,q.choice_c,q.choice_d].filter(Boolean);
  }, [q,isMatch]);

  const currentSelection = answers[q?.id];
  const multiSelection   = Array.isArray(currentSelection) ? currentSelection : [];

  const unanswered = useMemo(() =>
    questions.map((qs,i)=>({ i, id:qs.id })).filter(({ id }) => {
      const v = answers[id];
      return !v && v!==0 || (Array.isArray(v)&&v.length===0);
    }),
  [questions,answers]);

  useEffect(() => {
    if (learnMode||phase!=="exam") return;
    const h = e => { e.preventDefault(); e.returnValue=""; };
    window.addEventListener("beforeunload",h);
    return () => window.removeEventListener("beforeunload",h);
  }, [learnMode,phase]);

  // [visibilitychange hook déplacé après doSubmit]

  useEffect(() => {
    (async () => {
      try {
        const [eRes, qRes] = await Promise.all([
          API.get(`/exams/${examId}`),
          API.get(`/exams/${examId}/questions`),
        ]);
        setExam(eRes.data);
        setQuestions(qRes.data||[]);

        // ── Vérification limite de tentatives (mode test uniquement) ──
        if (!learnMode) {
          try {
            const cRes = await API.get(`/attempts/count/${examId}`);
            const info = cRes.data;
            setAttInfo(info);
            if (info.blocked) { setAttBlocked(true); return; }
          } catch { /* pas bloquant */ }
        }

        if (!learnMode) {
          // 1. Vérifier localStorage d'abord (plus rapide)
          const saved = store.load();
          if (saved?.startedAtMs && Object.keys(saved.answers||{}).length > 0) {
            setResumePrompt(true);
            setDraftLoaded(true);
            return;
          }
          // 2. Vérifier le draft API (autre navigateur / crash)
          try {
            const dRes = await API.get(`/attempts/draft/${examId}`);
            if (dRes.data && Object.keys(dRes.data.answers||{}).length > 0) {
              const draft = dRes.data;
              const startedMs = new Date(draft.started_at).getTime();
              store.save({ startedAtMs: startedMs, idx: 0, answers: draft.answers });
              setResumePrompt(true);
              setDraftLoaded(true);
              return;
            }
          } catch { /* pas de draft API */ }
        }
        const now = Date.now();
        setStartedAtMs(now);
        store.save({ startedAtMs:now, idx:0, answers:{} });
      } catch { setLoadError(t("errors.loadFailed")); }
    })();
  }, [examId,store,learnMode]);

  // ── Reprise après crash : l'utilisateur confirme ──────────────
  const handleResume = () => {
    const saved = store.load();
    if (saved?.startedAtMs) {
      if (typeof saved.idx==="number") setIdx(saved.idx);
      if (saved.answers) setAnswers(saved.answers);
      setStartedAtMs(saved.startedAtMs);
    }
    setResumePrompt(false);
  };

  const handleRestart = () => {
    store.clear();
    API.delete(`/attempts/draft/${examId}`).catch(()=>{});
    const now = Date.now();
    setStartedAtMs(now);
    setAnswers({});
    setIdx(0);
    store.save({ startedAtMs:now, idx:0, answers:{} });
    setResumePrompt(false);
  };

  // ── Sauvegarde périodique toutes les 30s vers l'API ───────────
  useEffect(() => {
    if (learnMode || phase !== "exam" || resumePrompt) return;
    const id = setInterval(() => {
      const answersSnapshot = answers;
      API.post("/attempts/draft", {
        exam_id:       examId,
        answers:       answersSnapshot,
        started_at_ms: startedAtMs,
      }).catch(() => {});
    }, 30_000);
    return () => clearInterval(id);
  }, [learnMode, phase, resumePrompt, answers, examId, startedAtMs]);

  useEffect(() => {
    if (phase==="exam") store.save({ idx, answers, startedAtMs });
  }, [idx,answers,startedAtMs,store,phase]);

  const onAnswer = (qid, val) => setAnswers(p => ({ ...p, [qid]:val }));

  const doSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true); setConfirmOpen(false); setError("");
    const allAnswers = { ...answers };
    if (questions) {
      questions.forEach(q => {
        const isLabQ = q.type === "lab" || q.question_type === "lab";
        if (isLabQ && !allAnswers[q.id]) allAnswers[q.id] = "__lab_skipped__";

        // ── Fix ordre mélangé : pour les questions multi-choix, les indices
        // stockés correspondent à l'ordre AFFICHÉ (mélangé côté serveur pour
        // anti-triche), qui diffère de l'ordre ORIGINAL en base utilisé pour
        // le calcul du score. On convertit donc les indices en TEXTES des
        // choix sélectionnés avant envoi — le texte reste valide quel que
        // soit l'ordre, contrairement à l'indice. (correct_indices n'est
        // jamais exposé au frontend pendant l'examen, donc on se fie au
        // type "multi" envoyé par QuestionExamView.)
        const isMultiQ = q.type === "multi";
        const qChoices = q.choices;
        if (isMultiQ && Array.isArray(allAnswers[q.id]) && Array.isArray(qChoices)) {
          allAnswers[q.id] = allAnswers[q.id]
            .map(i => qChoices[i])
            .filter(v => v !== undefined);
        }
      });
    }
    const answersList = Object.entries(allAnswers).map(([qid,value]) => ({ question_id:Number(qid), value }));
    try {
      const res = await API.post("/attempts/submit", { exam_id:examId, answers:answersList });
      store.clear();
      API.delete(`/attempts/draft/${examId}`).catch(()=>{});
      setServerResult(res.data);
      setPhase("report");
    } catch (e) {
      setSubmitting(false);
      setError(e?.response?.data?.detail||t("errors.saveFailed"));
    }
  }, [answers,examId,submitting,store,questions]);

  // ── Détection changement d'onglet (après doSubmit pour éviter TDZ) ──
  const maxTabSwitches = exam?.max_tab_switches ?? null;

  useEffect(() => {
    if (learnMode || phase !== "exam" || maxTabSwitches === null) return;

    const onHide = () => {
      if (document.visibilityState === "hidden") {
        setTabSwitches(prev => {
          const next = prev + 1;
          setTabWarning(true);
          if (next >= maxTabSwitches) {
            setTimeout(() => doSubmit(), 800);
          }
          return next;
        });
      }
    };

    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [learnMode, phase, maxTabSwitches, doSubmit]);

  const hasAnswer = () => {
    if (isMatch) return Object.values(answers[q?.id]||{}).filter(Boolean).length >= (q?.matching_lefts?.length||1);
    if (isMulti) return multiSelection.length>0;
    return Boolean(currentSelection);
  };

  const onValidate = async () => {
    if (!hasAnswer()) { setError("Veuillez sélectionner une réponse avant de valider."); return; }
    setError("");
    if (learnMode) {
      setChecking(true);
      try {
        const res = await API.post("/attempts/learn/check", {
          question_id: q.id,
          value: isMatch ? (answers[q.id] || {}) : isMulti ? multiSelection : currentSelection,
        });
        let resultData = res.data;
        if (isMulti && Array.isArray(res.data.correct_answer)) {
          const correctTexts  = new Set(res.data.correct_answer);
          const selectedTexts = new Set(multiSelection.map(i => choices[i]).filter(Boolean));
          const isReallyCorrect =
            correctTexts.size === selectedTexts.size &&
            [...correctTexts].every(t => selectedTexts.has(t));
          resultData = { ...res.data, correct: isReallyCorrect };
        }
        setLearnResult(resultData);
        setLearnDetails(prev => {
          const filtered = prev.filter(d => d.question_id !== q.id);
          return [...filtered, { question_id: q.id, correct: resultData.correct }];
        });
      } catch {
        setLearnResult(null);
      } finally {
        setChecking(false);
        setLearnFeedback(true);
        setValidated(true);
      }
      return;
    }
    setValidated(true);
  };

  const onNext = () => {
    if (learnMode && !validated) { onValidate(); return; }
    setLearnFeedback(false); setValidated(false); setLearnResult(null);
    const isLast = questions.length > 0 && idx === questions.length - 1;
    if (isLast) {
      if (learnMode) {
        const learnCorrect = learnDetails.filter(d => d.correct).length;
        const learnTotal   = questions.length;
        setServerResult({
          score:      learnCorrect,
          max_score:  learnTotal,
          percentage: learnTotal > 0 ? Math.round(learnCorrect / learnTotal * 100) : 0,
          details:    learnDetails,
        });
        setPhase("report");
      } else {
        setConfirmOpen(true);
      }
    } else {
      setIdx(i=>i+1);
    }
  };

  const onClearSelection = () => {
    setValidated(false); setLearnFeedback(false); setLearnResult(null);
    onAnswer(q.id, isMulti?[]:"");
  };

  // ── Guards ──────────────────────────────────────────────────────
  // ── Dialog reprise après crash ────────────────────────────────
  if (resumePrompt && !loadError) {
    const saved = store.load();
    const answeredCount = Object.keys(saved?.answers||{}).length;
    const savedDate = saved?.startedAtMs
      ? new Date(saved.startedAtMs).toLocaleString(i18n.language === "en" ? "en-US" : "fr-FR", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })
      : "";
    return (
      <Box sx={{ minHeight:"60vh", display:"flex", alignItems:"center", justifyContent:"center", px:2 }}>
        <Box sx={{ maxWidth:440, width:"100%" }}>
          <Box sx={{ background:P.navy, color:"white", px:3, py:2.5, borderRadius:"10px 10px 0 0", textAlign:"center" }}>
            <Typography sx={{ fontWeight:800, fontSize:"1.05rem" }}>
              {exam?.title || t("exams.title")}
            </Typography>
          </Box>
          <Box sx={{ background:"white", border:`1px solid ${P.slate[200]}`, borderTop:"none",
            borderRadius:"0 0 10px 10px", p:3 }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start"
                sx={{ p:2, borderRadius:"8px", background:alpha(P.amber,.08), border:`1px solid ${alpha(P.amber,.3)}` }}>
                <WarningAmberIcon sx={{ color:P.amber, fontSize:22, mt:.2, flexShrink:0 }} />
                <Box>
                  <Typography fontWeight={700} sx={{ color:P.slate[800], mb:.5 }}>
                    {t("exam.crashRecovery.title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("exam.crashRecovery.started")} <strong>{savedDate}</strong> · <strong>{answeredCount}</strong> {t("exam.crashRecovery.answered")}.
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={1.5}>
                <Button variant="contained" fullWidth startIcon={<ReplayIcon />}
                  onClick={handleResume}
                  sx={{ background:P.navy, "&:hover":{ background:P.navyL },
                    textTransform:"none", fontWeight:700, borderRadius:"8px", py:1.3 }}>
                  {t("exam.crashRecovery.resume")}
                </Button>
                <Button variant="outlined" fullWidth onClick={handleRestart}
                  sx={{ borderColor:P.slate[300], color:P.slate[600],
                    textTransform:"none", borderRadius:"8px", py:1.3 }}>
                  {t("exam.crashRecovery.restart")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Écran bloquant si limite atteinte ────────────────────────
  if (attBlocked && attInfo) {
    return (
      <Box sx={{ minHeight:"60vh", display:"flex", alignItems:"center", justifyContent:"center", px:2 }}>
        <Box sx={{ maxWidth:440, width:"100%" }}>
          <Box sx={{ background:P.navy, color:"white", px:3, py:2.5, borderRadius:"10px 10px 0 0", textAlign:"center" }}>
            <Typography sx={{ fontWeight:800, fontSize:"1.05rem" }}>
              {exam?.title || t("exams.title")}
            </Typography>
          </Box>
          <Box sx={{ background:"white", border:`1px solid ${P.slate[200]}`, borderTop:"none",
            borderRadius:"0 0 10px 10px", p:3 }}>
            <Stack spacing={2.5} alignItems="center">
              <Box sx={{ width:64, height:64, borderRadius:"50%", background:alpha("#ef4444",.1),
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Typography sx={{ fontSize:"1.8rem" }}>🚫</Typography>
              </Box>
              <Box sx={{ textAlign:"center" }}>
                <Typography variant="h6" fontWeight={800} color="#ef4444" gutterBottom>
                  {t("exam.blockedTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("exam.blockedDesc", { used: attInfo.used, max: attInfo.max })}
                </Typography>
              </Box>
              <Alert severity="info" sx={{ borderRadius:"8px", width:"100%" }}>
                {t("exam.blockedLearnMode")}
              </Alert>
              <Stack direction={{ xs:"column", sm:"row" }} spacing={1} sx={{ width:"100%" }}>
                <Button variant="outlined" fullWidth onClick={() => navigate(`/exams/${examId}?mode=learn`)}
                  sx={{ borderRadius:"8px", textTransform:"none", fontWeight:700, borderColor:P.navy, color:P.navy }}>
                  {t("exam.learnModeBtn")}
                </Button>
                <Button variant="outlined" fullWidth onClick={() => navigate("/exams")}
                  sx={{ borderRadius:"8px", textTransform:"none", borderColor:P.slate[300], color:P.slate[600] }}>
                  {t("exam.backToExams")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  if (loadError) return (
    <Alert severity="error" sx={{ m:3, borderRadius:2 }}>
      {loadError} <Button size="small" onClick={() => window.location.reload()}>{t("common.retry")}</Button>
    </Alert>
  );
  if (!exam) return (
    <Box sx={{ display:"flex", justifyContent:"center", py:14 }}>
      <CircularProgress sx={{ color:P.navy }} />
    </Box>
  );
  if (!questions.length) return (
    <Alert severity="info" sx={{ m:3, borderRadius:2 }}>{t("exams.noExams")}</Alert>
  );

  if (phase==="report") {
    return (
      <ExamReport
        exam={exam} questions={questions} answers={answers}
        result={serverResult}
        passThreshold={exam?.pass_threshold ?? 50}
        onRéessayer={() => { store.clear(); window.location.reload(); }}
        onGoResults={() => navigate("/results")}
        t={t}
      />
    );
  }

  // Auto-submit on time up
  const elapsed = Math.floor((Date.now()-startedAtMs)/1000);
  if (!learnMode && elapsed >= durationMinutes*60 && !submitting) {
    doSubmit();
    return <Typography sx={{ p:3 }}>{t("exam.timeUp")}</Typography>;
  }

  const ansCount = Object.keys(answers).length;

  return (
    <Box sx={{ maxWidth:900, mx:"auto", px:{ xs:0, sm:0 } }}>
      {/* Bannière mode apprentissage */}
      {learnMode && (
        <Alert icon={<MenuBookIcon />} severity="info"
          sx={{ mb:2, borderRadius:"8px", mx:{ xs:1, sm:0 } }}>
          <strong>{t("exam.learnMode")}</strong> — {t("exam.learnModeDesc")}
        </Alert>
      )}

      {/* ── Warning changement d'onglet ── */}
      {!learnMode && maxTabSwitches !== null && tabWarning && (
        <Alert
          severity={tabSwitches >= maxTabSwitches ? "error" : "warning"}
          onClose={() => setTabWarning(false)}
          sx={{ mb:2, borderRadius:"8px", mx:{ xs:1, sm:0 }, fontWeight:600 }}
        >
          {tabSwitches >= maxTabSwitches ? (
            <>⚠️ {t("exam.tabLimitReached")}</>
          ) : (
            <>
              ⚠️ {t("exam.tabWarning")}&nbsp;
              (<strong>{tabSwitches}/{maxTabSwitches}</strong>).&nbsp;
              {maxTabSwitches - tabSwitches === 1
                ? t("exam.tabWarningLast")
                : t("exam.tabWarningMore", { count: maxTabSwitches - tabSwitches })
              }
            </>
          )}
        </Alert>
      )}

      {/* Header navy */}
      <ExamHeader
        title={exam.title}
        learnMode={learnMode}
        startedAtMs={startedAtMs}
        durationMinutes={durationMinutes}
        onFinish={() => setConfirmOpen(true)}
        onTimeUp={doSubmit}
        tabSwitches={tabSwitches}
        maxTabSwitches={maxTabSwitches}
        t={t}
      />

      {/* Corps de la question */}
      <QuestionCard
        q={q} idx={idx} questions={questions}
        isMatch={isMatch} isMulti={isMulti} isLab={isLab}
        choices={choices} answers={answers}
        multiSelection={multiSelection} currentSelection={currentSelection}
        learnMode={learnMode} validated={validated}
        learnFeedback={learnFeedback} learnResult={learnResult}
        checking={checking} error={error}
        unanswered={unanswered} setIdx={setIdx}
        flagged={flagged}
        onFlag={qid => setFlagged(prev => {
          const next = new Set(prev);
          next.has(qid) ? next.delete(qid) : next.add(qid);
          return next;
        })}
        onAnswer={onAnswer} onValidate={onValidate}
        onNext={onNext} onClearSelection={onClearSelection}
        startedAtMs={startedAtMs} durationMinutes={durationMinutes}
        doSubmit={doSubmit}
        t={t}
      />

      {/* Dialog confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        answered={ansCount}
        total={questions.length}
        skipped={questions.length - ansCount}
        onConfirm={doSubmit}
        onCancel={() => setConfirmOpen(false)}
        submitting={submitting}
        t={t}
      />
    </Box>
  );
}